import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Star, MapPin, MessageSquare, Trash2, Shield, Eye, Pencil, Truck } from "lucide-react";
import { toast } from "sonner";
import { differenceInCalendarDays } from "date-fns";

const resolveImg = (img) =>
  !img ? "https://images.unsplash.com/photo-1581147036324-c1c89c2c8b5c?w=1000&q=80"
       : img.startsWith("http") ? img : fileUrl(img);

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [range, setRange] = useState();
  const [activeImg, setActiveImg] = useState(0);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feePct, setFeePct] = useState(0);

  const load = () => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => navigate("/browse"));
    api.get(`/listings/${id}/reviews`).then((r) => setReviews(r.data));
  };
  useEffect(load, [id]);
  useEffect(() => { api.get("/config").then((r) => setFeePct(r.data.buyer_fee_percent)).catch(() => {}); }, []);

  if (!listing) return <div className="py-32 flex justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>;

  const days = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) + 1 : 0;
  const rental = days * (listing.price_per_day || 0);
  const serviceFee = Math.round(rental * (feePct / 100) * 100) / 100;
  const deposit = listing.requires_deposit ? (listing.deposit_amount || 0) : 0;
  const total = (rental + serviceFee + deposit).toFixed(2);
  const isOwner = user?.user_id === listing.owner_id;
  const canRent = ["rent", "both"].includes(listing.listing_type) && !listing.is_sold;
  const canSale = ["sale", "both"].includes(listing.listing_type) && !listing.is_sold;

  const requestBooking = async () => {
    if (!user) { navigate("/login"); return; }
    if (!range?.from || !range?.to) { toast.error("Select rental dates first"); return; }
    setSubmitting(true);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      const res = await api.post("/bookings", {
        listing_id: listing.id,
        start_date: fmt(range.from),
        end_date: fmt(range.to),
      });
      toast.success("Request sent! You'll be able to pay once the owner accepts.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    } finally { setSubmitting(false); }
  };

  const messageOwner = () => {
    if (!user) { navigate("/login"); return; }
    navigate(`/messages/${listing.owner_id}`);
  };

  const buyNow = async () => {
    if (!user) { navigate("/login"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/purchases/checkout", { listing_id: listing.id, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const submitReview = async () => {
    if (!user) { navigate("/login"); return; }
    if (!comment.trim()) { toast.error("Write a comment"); return; }
    try {
      await api.post("/reviews", { listing_id: id, rating, comment });
      setComment(""); setRating(5);
      toast.success("Review posted");
      load();
    } catch { toast.error("Failed to post review"); }
  };

  const deleteListing = async () => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing deleted"); navigate("/dashboard"); }
    catch { toast.error("Failed to delete"); }
  };

  const images = listing.images?.length ? listing.images : [null];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="grid lg:grid-cols-3 gap-10">
        {/* Left: gallery + info */}
        <div className="lg:col-span-2">
          <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-black/10 bg-[#F4F1EC]">
            <img data-testid="listing-main-image" src={resolveImg(images[activeImg])} alt={listing.title} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[#FF5A00]" : "border-transparent"}`}>
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00]">
              {(listing.categories && listing.categories.length ? listing.categories : [listing.category]).join(" · ")}
            </span>
            {listing.condition && (
              <span data-testid="listing-condition" className="ml-2 text-[11px] font-bold uppercase tracking-wider bg-[#E3E8E1] text-[#0A0A0A] px-2 py-0.5 rounded-full align-middle">{listing.condition}</span>
            )}
            <h1 data-testid="listing-title" className="font-display text-4xl font-bold tracking-tight mt-2">{listing.title}</h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.location}</span>
              {listing.review_count > 0 && (
                <span className="flex items-center gap-1 font-semibold text-foreground"><Star className="w-4 h-4 fill-[#0A0A0A]" /> {listing.avg_rating} ({listing.review_count})</span>
              )}
            </div>
            <p className="mt-6 text-base leading-relaxed whitespace-pre-line">{listing.description}</p>
          </div>

          {isOwner && (
            <div data-testid="owner-stats-bar" className="mt-6 flex items-center justify-between bg-[#F4F1EC] border border-black/10 rounded-2xl px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium">
                <Eye className="w-4 h-4 text-[#FF5A00]" /> {listing.views || 0} {listing.views === 1 ? "view" : "views"} on this ad
              </span>
              <button
                data-testid="edit-listing-btn"
                onClick={() => navigate(`/edit/${listing.id}`)}
                className="flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#FF5A00] transition-colors active:scale-95"
              >
                <Pencil className="w-4 h-4" /> Edit listing
              </button>
            </div>
          )}

          {/* Owner */}
          <div className="mt-8 flex items-center justify-between bg-white border border-black/10 rounded-2xl p-4">
            <Link to={`/user/${listing.owner_id}`} data-testid="owner-profile-link" className="flex items-center gap-3 group">
              <Avatar className="w-11 h-11 border border-black/10">
                <AvatarImage src={listing.owner_picture} />
                <AvatarFallback className="bg-[#E3E8E1] font-semibold">{listing.owner_name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Owner</p>
                <p className="font-semibold group-hover:text-[#FF5A00] transition-colors">{listing.owner_name}</p>
                {listing.owner_review_count > 0 ? (
                  <span className="flex items-center gap-1 text-xs font-medium mt-0.5">
                    <Star className="w-3.5 h-3.5 fill-[#FF5A00] text-[#FF5A00]" /> {listing.owner_rating}
                    <span className="text-muted-foreground">({listing.owner_review_count})</span>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">New member</span>
                )}
              </div>
            </Link>
            {!isOwner && (
              <button data-testid="message-owner-btn" onClick={messageOwner} className="flex items-center gap-2 bg-white border border-black/20 rounded-full px-4 py-2 text-sm font-medium hover:border-black transition-colors">
                <MessageSquare className="w-4 h-4" /> Message
              </button>
            )}
          </div>

          {/* Reviews */}
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight">Reviews</h2>
            {user && !isOwner && (
              <div className="mt-4 bg-white border border-black/10 rounded-2xl p-4">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                      <Star className={`w-6 h-6 ${n <= rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-black/20"}`} />
                    </button>
                  ))}
                </div>
                <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="bg-white border-black/10 rounded-xl" />
                <button data-testid="submit-review-btn" onClick={submitReview} className="mt-3 bg-[#0A0A0A] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#FF5A00] transition-colors">Post review</button>
              </div>
            )}
            <div className="mt-4 space-y-4">
              {reviews.length === 0 && <p className="text-muted-foreground text-sm">No reviews yet.</p>}
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9"><AvatarImage src={r.user_picture} /><AvatarFallback className="bg-[#E3E8E1] text-sm">{r.user_name?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-semibold text-sm">{r.user_name}</p>
                      <div className="flex gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-black/15"}`} />)}</div>
                    </div>
                  </div>
                  <p className="mt-2 text-sm">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: booking / buy */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-black/10 rounded-2xl p-5">
            <div className="flex flex-col gap-1">
              {["rent", "both"].includes(listing.listing_type) && (
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-3xl font-bold">€{listing.price_per_day}</span>
                  <span className="text-muted-foreground">/ day</span>
                </div>
              )}
              {["sale", "both"].includes(listing.listing_type) && (
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-2xl font-bold text-[#FF5A00]">€{listing.sale_price}</span>
                  <span className="text-muted-foreground">to buy</span>
                </div>
              )}
            </div>

            {listing.is_sold && (
              <div data-testid="sold-banner" className="mt-4 bg-[#0A0A0A] text-white rounded-xl p-4 text-center font-semibold">
                This item has been sold
              </div>
            )}

            {isOwner ? (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">This is your listing.</p>
                <button data-testid="delete-listing-btn" onClick={deleteListing} className="mt-3 w-full flex items-center justify-center gap-2 border border-destructive/40 text-destructive rounded-full py-2.5 text-sm font-medium hover:bg-destructive/5 transition-colors">
                  <Trash2 className="w-4 h-4" /> Delete listing
                </button>
              </div>
            ) : listing.is_sold ? null : (
              <>
                {canRent && (
                  <>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mt-5 mb-2">Select rental dates</p>
                    <div className="flex justify-center border border-black/10 rounded-xl p-2">
                      <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={{ before: new Date() }} data-testid="booking-calendar" />
                    </div>
                    {days > 0 && (
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">€{listing.price_per_day} × {days} day{days > 1 ? "s" : ""}</span><span className="font-semibold">€{rental.toFixed(2)}</span></div>
                        {serviceFee > 0 && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Service fee</span><span className="font-semibold" data-testid="service-fee-line">€{serviceFee.toFixed(2)}</span></div>
                        )}
                        {deposit > 0 && (
                          <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Refundable deposit</span><span className="font-semibold" data-testid="deposit-line">€{deposit.toFixed(2)}</span></div>
                        )}
                        <div className="flex justify-between border-t border-black/10 pt-2 font-display text-lg font-bold"><span>Total today</span><span data-testid="booking-total">€{total}</span></div>
                        {deposit > 0 && <p className="text-xs text-muted-foreground">Deposit is refunded when the owner marks the item returned.</p>}
                      </div>
                    )}
                    <button data-testid="request-rent-btn" onClick={requestBooking} disabled={submitting} className="mt-4 w-full bg-[#0A0A0A] text-white rounded-full py-3 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
                      {submitting ? "Creating…" : "Request to rent"}
                    </button>
                  </>
                )}
                {canRent && canSale && (
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-black/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-black/10" />
                  </div>
                )}
                {canSale && (
                  <>
                    <button data-testid="buy-now-btn" onClick={buyNow} disabled={submitting} className="w-full bg-[#FF5A00] text-white rounded-full py-3 font-medium hover:bg-[#E04F00] transition-colors active:scale-95 disabled:opacity-60">
                      {submitting ? "Redirecting…" : `Buy now — €${listing.sale_price}`}
                    </button>
                    {feePct > 0 && <p className="text-xs text-muted-foreground text-center mt-2">+ {feePct}% service fee added at checkout</p>}
                  </>
                )}
              </>
            )}
            <div data-testid="delivery-notice" className="mt-4 flex items-start gap-2.5 bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-xl p-3.5">
              <Truck className="w-4 h-4 text-[#FF5A00] mt-0.5 shrink-0" />
              <div>
                {listing.delivery_options?.length > 0 && (
                  <div data-testid="delivery-options" className="flex flex-wrap gap-1.5 mb-2">
                    {listing.delivery_options.map((o) => (
                      <span key={o} className="text-[11px] font-semibold bg-white border border-[#FF5A00]/30 text-[#FF5A00] rounded-full px-2 py-0.5">{o}</span>
                    ))}
                  </div>
                )}
                <p className="text-xs leading-relaxed text-[#0A0A0A]">
                  <span className="font-semibold">Delivery &amp; pickup are arranged directly with the owner.</span>{" "}
                  SelLend doesn't handle shipping — once your request is accepted, message the owner to agree on postage, collection, or drop-off.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
